/**
 * Flight route + airport-name helpers shared by every surface that renders
 * a multi-segment flight voucher (operational voucher body, public voucher
 * page header, itinerary flight card, future trip-bundle cover).
 *
 * Keep this file pure — no React, no axios, no DOM access — so it can run
 * inside Playwright PDF render contexts and Jest unit tests without setup.
 */

// ---------------------------------------------------------------------------
// deriveTripType
//
// The LLM-extracted `flight.tripType` has been observed to mislabel
// one-way-with-connection as 'multi-city' or 'round-trip'. Industry
// definition is simple: a round-trip is a closed loop — first segment's
// departure airport code EXACTLY equals last segment's arrival airport
// code. Anything else is one-way (with or without connection). We collapse
// the academic "multi-city" classification into "one-way" because the
// customer-facing route string already communicates the stops.
// ---------------------------------------------------------------------------
export function deriveTripType(segments) {
  if (!Array.isArray(segments) || segments.length === 0) return null;
  if (segments.length === 1) return 'one-way';
  const first = segments[0] || {};
  const last = segments[segments.length - 1] || {};
  const dep = (first.departureAirportCode || '').trim().toUpperCase();
  const arr = (last.arrivalAirportCode || '').trim().toUpperCase();
  if (dep && arr && dep === arr) return 'round-trip';
  return 'one-way';
}

// ---------------------------------------------------------------------------
// buildHeaderRoute
//
// Returns a concise route string suitable for hero titles and page headers:
//   - one-way 1 leg:        "PNQ – CCU"
//   - one-way 2 legs:       "PNQ – CCU via DEL"
//   - one-way 3+ legs:      "DEL – JFK via DXB, LHR"
//   - round-trip:           "BOM – DXB – BOM"
//
// Falls back to city names when IATA codes are missing on every segment
// (e.g. legacy uploads). Returns null when even cities are unavailable so
// the caller can render a sensible default ("Flight Voucher" etc.).
// ---------------------------------------------------------------------------
export function buildHeaderRoute(segments) {
  if (!Array.isArray(segments) || segments.length === 0) return null;
  const first = segments[0] || {};
  const last = segments[segments.length - 1] || {};
  const origin = first.departureAirportCode || first.departureCity || '';
  const finalArr = last.arrivalAirportCode || last.arrivalCity || '';
  if (!origin || !finalArr) return null;
  const tripType = deriveTripType(segments);
  if (tripType === 'round-trip' && segments.length >= 2) {
    const turnaround = segments[0].arrivalAirportCode || segments[0].arrivalCity || '';
    if (turnaround) return `${origin} – ${turnaround} – ${origin}`;
    return `${origin} – ${origin}`;
  }
  if (segments.length === 1) return `${origin} – ${finalArr}`;
  // Build the "via" list of intermediate stop codes. Defensive filter:
  // exclude any via code that exactly equals the origin or the final
  // destination — those are extraction errors where the LLM stamped the
  // final-destination code onto an intermediate segment's arrival. Without
  // this filter we produce nonsense like "PNQ – CCU via CCU".
  const viaCodes = segments
    .slice(0, -1)
    .map((s) => s.arrivalAirportCode || s.arrivalCity || '')
    .filter((code) => code && code !== origin && code !== finalArr);
  if (viaCodes.length === 0) return `${origin} – ${finalArr}`;
  return `${origin} – ${finalArr} via ${viaCodes.join(', ')}`;
}

// ---------------------------------------------------------------------------
// formatAirportName
//
// Normalises the messy ALL-CAPS airport names the OCR extracts into a
// readable "{City}, {Airport Name} [Intl ]Airport" shape.
//
// Examples:
//   "KOLKATA SUBHAS CHANDRA BOSE"  →  "Kolkata, Subhas Chandra Bose Airport"
//   "DELHI INDRA GANDHI INTL"      →  "Delhi, Indra Gandhi Intl Airport"
//   "PUNE LOHEGAON"                →  "Pune, Lohegaon Airport"
//   "LONDON HEATHROW AIRPORT"      →  "London, Heathrow Airport"   (no dup)
//
// We do NOT attempt spell-correction here ("Indra" stays "Indra") — that
// belongs to the extraction prompt, not the render layer. Edge cases like
// single-word inputs ("DXB") get returned title-cased + " Airport".
// ---------------------------------------------------------------------------
export function formatAirportName(raw) {
  if (!raw) return '';
  const cleaned = String(raw).trim();
  if (!cleaned) return '';

  const hasIntl = /\b(INTL|INTERNATIONAL)\b/i.test(cleaned);
  const hasAirport = /\bAIRPORT\b/i.test(cleaned);

  // Strip the structural words so we can title-case the proper noun part
  // cleanly and re-append the suffix in canonical form.
  let core = cleaned
    .replace(/\b(INTL|INTERNATIONAL)\b/gi, '')
    .replace(/\bAIRPORT\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  core = core.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

  const parts = core.split(' ').filter(Boolean);
  if (parts.length === 0) return '';

  const city = parts[0];
  const restName = parts.slice(1).join(' ');
  const intlSuffix = hasIntl ? ' Intl' : '';
  const airportSuffix = hasAirport || !restName ? ' Airport' : ' Airport';
  // ^ always append "Airport" — even when source had it, we already
  //   stripped it above, so re-appending preserves the canonical shape.

  if (!restName) {
    return `${city}${intlSuffix}${airportSuffix}`.trim();
  }
  return `${city}, ${restName}${intlSuffix}${airportSuffix}`.trim();
}
